import * as yup from 'yup';
import dayjs from 'dayjs';
import type { WorkAgreementDetails } from 'components/types';

export const dayLimitArrangementSchema = (
  translate: (key: string, opts?: any) => string,
  workAgreements?: WorkAgreementDetails[]
) =>
  yup.object().shape({
    validFrom: yup
      .date()
      .required(
        translate('validationFieldRequired', {
          name: translate('dayLimitArrangements.form.validFrom')
        })
      )
      .typeError(
        translate('validationFieldRequired', {
          name: translate('dayLimitArrangements.form.validFrom')
        })
      )
      .nullable(),
    validTo: yup
      .date()
      .required(
        translate('validationFieldRequired', {
          name: translate('dayLimitArrangements.form.validTo')
        })
      )
      .nullable()
      .min(
        yup.ref('validFrom'),
        translate('validationNotBefore', {
          first: translate('dayLimitArrangements.form.validFrom'),
          last: translate('dayLimitArrangements.form.validTo')
        })
      )
      .transform((v, o) => (o === '' ? null : v))
      .test(
        'overlaps-employment',
        translate('dayLimitArrangements.form.doesNotOverlapEmployment'),
        function (value) {
          const { employmentId, validFrom } = this.parent;
          if (!employmentId || !validFrom || !value) return true;
          const wa = workAgreements?.find((w) => w.entityId === employmentId);
          if (!wa) return true;
          const arrStart = dayjs(validFrom);
          const arrEnd = dayjs(value);
          const empStart = wa.employmentStart
            ? dayjs(wa.employmentStart)
            : null;
          const empEnd = wa.employmentEnd ? dayjs(wa.employmentEnd) : null;
          if (empStart && arrEnd.isBefore(empStart, 'day')) return false;
          if (empEnd && arrStart.isAfter(empEnd, 'day')) return false;
          return true;
        }
      ),
    maxNumberOfDays: yup
      .number()
      .nullable()
      .when('dayLimitArrangementNotApplicableForEmployee', {
        is: false,
        then: (schema) =>
          schema
            .required(
              translate('validationFieldRequired', {
                name: translate('dayLimitArrangements.form.maxNumberOfDays')
              })
            )
            .min(1, translate('dayLimitArrangements.form.minDaysValidation'))
      }),
    dayLimitArrangementNotApplicableForEmployee: yup.boolean(),
    alternativePeriod: yup.boolean(),
    alternativeTimeUnit: yup
      .string()
      .nullable()
      .when('alternativePeriod', {
        is: true,
        then: (schema) =>
          schema.required(
            translate('validationFieldRequired', {
              name: translate('dayLimitArrangements.form.alternativeTimeUnit')
            })
          )
      }),
    alternativeNrOfPeriods: yup
      .number()
      .nullable()
      .when(['alternativePeriod', 'alternativeTimeUnit'], {
        is: (alternativePeriod: boolean, alternativeTimeUnit: string) =>
          alternativePeriod && alternativeTimeUnit === 'CUSTOM',
        then: (schema) =>
          schema
            .required(
              translate('validationFieldRequired', {
                name: translate(
                  'dayLimitArrangements.form.alternativeNrOfPeriods'
                )
              })
            )
            .min(1, translate('dayLimitArrangements.form.minWeeksValidation'))
      }),
    startDateForAlternativePeriod: yup
      .date()
      .nullable()
      .when(['alternativePeriod', 'alternativeTimeUnit'], {
        is: (alternativePeriod: boolean, alternativeTimeUnit: string) =>
          alternativePeriod && alternativeTimeUnit === 'CUSTOM',
        then: (schema) =>
          schema
            .required(
              translate('validationFieldRequired', {
                name: translate(
                  'dayLimitArrangements.form.startDateForAlternativePeriod'
                )
              })
            )
            .min(
              yup.ref('validFrom'),
              translate('validationNotBefore', {
                first: translate('dayLimitArrangements.form.validFrom'),
                last: translate(
                  'dayLimitArrangements.form.startDateForAlternativePeriod'
                )
              })
            )
            .max(
              yup.ref('validTo'),
              translate('validationNotAfter', {
                first: translate(
                  'dayLimitArrangements.form.startDateForAlternativePeriod'
                ),
                last: translate('dayLimitArrangements.form.validTo')
              })
            )
            .test(
              'is-monday',
              translate('dayLimitArrangements.form.mustBeMonday'),
              (value) => {
                if (!value) return true;
                const date = new Date(value);
                return date.getDay() === 1;
              }
            )
      })
  });
